/**
 * The card page's Prices tab (TMMPDM-0089, tmm-api 0154). A DRAFT version's three columns — our cost,
 * wholesale (the reseller's cost) and retail (the customer's price) — for the base, the annual base,
 * guests, bank connections and AI, plus the AI wallet terms and trials. Guests and connections are
 * "included + per unit": the editor writes exactly two bands. A version with any other band shape
 * shows its bands read-only and the editor never sends them. A published version is read-only.
 * The console checks only an input's own shape; the card's rules (wholesale ≤ retail and the rest)
 * are the server's, placed on their inputs with catalog words. Card processing isn't a card term
 * (Product settings), and our annual cost is the server's (monthly × 12), so neither is an input.
 * TMMPDM-0090 (tmm-api 0155): the version's `violations` lead the tab as a warning in catalog words.
 * Every save sends the wholesale side (the base, the annual, the simple bands' wholesale and
 * `aiWholesaleMarkupBp`); a tiered table is still omitted, and an absent band table stays as it is.
 */
import { useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { adminApi } from '@/api/tmm/admin';
import {
  ApiError,
  errorCopy,
  isStaleWrite,
  isValidationFailed,
  validationFields,
} from '@/api/errors';
import type {
  AdminBandView,
  CustomerRateCardVersionView,
  CustomerRateCardVersionWrite,
} from '@/api/tmm/types';
import {
  ConfirmDialog,
  FormField,
  FormPage,
  FormRow,
  FormSection,
  type ErrorSummaryItem,
} from '@/components/page';
import { Amount } from '@/components/ui';
import { useCopy } from '@/hooks/useCopy';
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard';
import { formatMoney, formatPercentBp, majorFromMinor, NOT_SENT } from '@/format';
import { useTranslation } from 'react-i18next';
import { simpleBandInputs, simpleBands } from '@/routes/customerCards/customerRateCardModel';
import { FieldWithHelp } from './FieldWithHelp';
import { invalidInt, invalidMoney, invalidPct, invalidPercent, parseMajor } from './rateCardInputs';
import { PRICE_KEYS, priceKeyOf, priceLabel, violationLines, type PriceKey } from './pricing';

const VALUE_WIDTH = '24ch';

type CreditBy = 'retail' | 'cost';

const fieldId = (key: string) => `rate-card-price-${key}`;

function withoutKey(messages: Record<string, string>, key: string): Record<string, string> {
  if (!(key in messages)) return messages;
  const { [key]: _dropped, ...rest } = messages;
  return rest;
}

const MONEY_KEYS: readonly PriceKey[] = [
  'baseCostMinor',
  'baseWholesaleMinor',
  'baseMinor',
  'guestCost',
  'guestWholesale',
  'guestRetail',
  'connectionCost',
  'connectionWholesale',
  'connectionRetail',
  'aiMonthlyCredit',
  'aiFirstPurchaseMinor',
  'aiRechargeTriggerMinor',
  'aiRechargeAmountMinor',
];

function initialValues(
  version: CustomerRateCardVersionView,
  currency: string,
): Record<PriceKey, string> {
  const money = (minor: number) => majorFromMinor(minor, currency);
  const guests = simpleBands(version.seatBands);
  const connections = simpleBands(version.connectedAccountBands);
  return {
    baseCostMinor: money(version.baseCostMinor),
    baseWholesaleMinor: money(version.baseWholesaleMinor),
    baseMinor: money(version.baseMinor),
    annualBaseWholesaleMinor:
      version.annualBaseMinor === null ? '' : money(version.annualBaseWholesaleMinor),
    annualBaseMinor: version.annualBaseMinor === null ? '' : money(version.annualBaseMinor),
    guestsIncluded: guests ? String(guests.included) : '',
    guestCost: guests ? money(guests.perUnit.costMinor) : '',
    guestWholesale: guests ? money(guests.perUnit.wholesaleMinor) : '',
    guestRetail: guests ? money(guests.perUnit.retailMinor) : '',
    connectionsIncluded: connections ? String(connections.included) : '',
    connectionCost: connections ? money(connections.perUnit.costMinor) : '',
    connectionWholesale: connections ? money(connections.perUnit.wholesaleMinor) : '',
    connectionRetail: connections ? money(connections.perUnit.retailMinor) : '',
    aiWholesaleMarkupBp: String(version.aiWholesaleMarkupBp / 100),
    aiRetailMarkupBp: String(version.aiRetailMarkupBp / 100),
    aiMonthlyCredit: money(version.aiMonthlyCreditMinor),
    aiCreditPlatformShareBp: String(version.aiCreditPlatformShareBp / 100),
    aiFirstPurchaseMinor: money(version.aiFirstPurchaseMinor),
    aiRechargeTriggerMinor: money(version.aiRechargeTriggerMinor),
    aiRechargeAmountMinor: money(version.aiRechargeAmountMinor),
    trialDaysNewSubscriber: String(version.trialDaysNewSubscriber),
    trialDaysGuestConversion: String(version.trialDaysGuestConversion),
  };
}

export function PricesTab({
  cardId,
  currency,
  version,
  serverErrors,
  onSaved,
  onReload,
}: {
  cardId: number;
  currency: string;
  version: CustomerRateCardVersionView;
  /** The last publish's refusal, placed on its inputs (409 `RATE_CARD_INVALID`). */
  serverErrors?: Partial<Record<PriceKey, string>>;
  onSaved: (saved: CustomerRateCardVersionView) => void;
  onReload: () => void;
}) {
  const { t } = useCopy();
  if (version.status !== 'DRAFT') {
    return (
      <>
        <VersionViolations version={version} />
        <Alert severity="info">
          {t(
            'rateCard.prices.readOnly',
            'A published version is read-only. Use New version… to change its prices.',
          )}
        </Alert>
        <PricesSummary version={version} currency={currency} />
      </>
    );
  }
  return (
    <>
      <VersionViolations version={version} />
      <PricesForm
        key={`${version.id}:${version.version}`}
        cardId={cardId}
        currency={currency}
        version={version}
        serverErrors={serverErrors ?? {}}
        onSaved={onSaved}
        onReload={onReload}
      />
    </>
  );
}

/**
 * The version's `violations` (TMMAPI-0155), the server's verdict: a warning with
 * the server's count and one line per violation in catalog words. Nothing when the list is empty
 * or absent; the console adds no rule of its own.
 */
function VersionViolations({ version }: { version: CustomerRateCardVersionView }) {
  const { t } = useCopy();
  const violations = version.violations ?? [];
  if (violations.length === 0) return null;
  const lines = violationLines(violations, t, {
    guests: simpleBands(version.seatBands) !== null,
    connections: simpleBands(version.connectedAccountBands) !== null,
  });
  return (
    <Alert severity="warning">
      <Typography variant="body2" component="p" sx={{ fontWeight: 600 }}>
        {version.status === 'DRAFT'
          ? t(
              'rateCard.violations.summary',
              "This version has {{count, number}} problems; publishing will be refused until they're fixed.",
              { count: violations.length },
            )
          : // A published version that breaks a rule keeps selling (tmm-api 0155); a new version fixes it.
            t(
              'rateCard.violations.summaryPublished',
              'This version breaks {{count, number}} publishing rules. It keeps selling; fix them in a new version.',
              { count: violations.length },
            )}
      </Typography>
      <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
        {lines.map((line, index) => (
          <Typography component="li" variant="body2" key={index}>
            {line}
          </Typography>
        ))}
      </Box>
    </Alert>
  );
}

function PricesForm({
  cardId,
  currency,
  version,
  serverErrors,
  onSaved,
  onReload,
}: {
  cardId: number;
  currency: string;
  version: CustomerRateCardVersionView;
  serverErrors: Partial<Record<PriceKey, string>>;
  onSaved: (saved: CustomerRateCardVersionView) => void;
  onReload: () => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const guestsSimple = simpleBands(version.seatBands) !== null;
  const connectionsSimple = simpleBands(version.connectedAccountBands) !== null;
  const [values, setValues] = useState(() => initialValues(version, currency));
  const [creditBy, setCreditBy] = useState<CreditBy>('retail');
  const [attempt, setAttempt] = useState(
    serverErrors && Object.keys(serverErrors).length > 0 ? 1 : 0,
  );
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>(() => ({ ...serverErrors }));
  const [formError, setFormError] = useState<{ message: string; stale: boolean } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const inFlight = useRef(false);

  const snapshot = JSON.stringify([values, creditBy]);
  const [initialSnapshot, setInitialSnapshot] = useState(snapshot);
  const dirty = snapshot !== initialSnapshot;
  const title = t('rateCard.prices.editTitle', 'Prices of version {{version, number}}', {
    version: version.versionNo,
  });
  const guard = useUnsavedChangesGuard(dirty, title);

  const update = (key: PriceKey, value: string) => {
    setValues((prev) => ({ ...prev, [key]: value }));
    setSaveErrors((prev) => withoutKey(prev, key));
  };

  const labels = Object.fromEntries(PRICE_KEYS.map((key) => [key, priceLabel(key, t)])) as Record<
    PriceKey,
    string
  >;
  if (creditBy === 'cost') {
    labels.aiMonthlyCredit = t('rateCard.prices.creditAtCost', 'Monthly AI credit, at our cost');
  }

  const help: Record<PriceKey, string> = {
    baseCostMinor: t(
      'rateCard.prices.help.ourCost',
      'What one subscriber costs us each month. Only the product admin sees it; a loss is allowed and shows on the worksheet.',
    ),
    baseWholesaleMinor: t(
      'rateCard.prices.help.wholesale',
      'What the reseller pays us each month for one subscriber.',
    ),
    baseMinor: t('rateCard.prices.help.retail', 'What the customer pays each month.'),
    annualBaseWholesaleMinor: t(
      'rateCard.prices.help.annualWholesale',
      "What the reseller pays us for a year's plan.",
    ),
    annualBaseMinor: t(
      'rateCard.prices.help.annualRetail',
      'What the customer pays for a year. Leave empty to offer monthly only.',
    ),
    guestsIncluded: t(
      'rateCard.prices.help.included',
      'How many are free to the customer and to the reseller. We carry their cost.',
    ),
    guestCost: t('rateCard.prices.help.unitCost', 'What each one costs us, included or not.'),
    guestWholesale: t(
      'rateCard.prices.help.unitWholesale',
      'What the reseller pays us for each one after the included count.',
    ),
    guestRetail: t(
      'rateCard.prices.help.unitRetail',
      'What the customer pays for each one after the included count.',
    ),
    connectionsIncluded: t(
      'rateCard.prices.help.included',
      'How many are free to the customer and to the reseller. We carry their cost.',
    ),
    connectionCost: t('rateCard.prices.help.unitCost', 'What each one costs us, included or not.'),
    connectionWholesale: t(
      'rateCard.prices.help.unitWholesale',
      'What the reseller pays us for each one after the included count.',
    ),
    connectionRetail: t(
      'rateCard.prices.help.unitRetail',
      'What the customer pays for each one after the included count.',
    ),
    aiWholesaleMarkupBp: t(
      'rateCard.prices.help.aiWholesaleMarkup',
      "Our markup on the AI provider's cost: the reseller's AI price.",
    ),
    aiRetailMarkupBp: t(
      'rateCard.prices.help.aiRetailMarkup',
      "The markup on the wholesale AI price: the customer's AI price.",
    ),
    aiMonthlyCredit: t(
      'rateCard.prices.help.credit',
      'AI the customer gets each month. Set it at the customer price or by what it costs us; we convert our cost with the markups and store the customer price.',
    ),
    aiCreditPlatformShareBp: t(
      'rateCard.prices.help.creditShare',
      'How much of the credit we absorb. The reseller absorbs the rest.',
    ),
    aiFirstPurchaseMinor: t(
      'rateCards.help.aiFirstPurchase.short',
      'The smallest first AI purchase. It must be more than the recharge trigger.',
    ),
    aiRechargeTriggerMinor: t(
      'rateCards.help.aiRechargeTrigger.short',
      'The wallet recharges when the balance falls below this.',
    ),
    aiRechargeAmountMinor: t(
      'rateCards.help.aiRechargeAmount.short',
      'How much each automatic recharge adds.',
    ),
    trialDaysNewSubscriber: t(
      'rateCards.help.trialNew.short',
      'Free days before a new subscriber is first charged.',
    ),
    trialDaysGuestConversion: t(
      'rateCards.help.trialGuest.short',
      'Free days when a guest becomes a subscriber.',
    ),
  };

  const validate = (): Record<string, string> => {
    const next: Record<string, string> = {};
    const nonNeg = t('rateCards.form.nonNegative', 'Must be zero or greater.');
    for (const key of MONEY_KEYS) {
      const isGuest = key.startsWith('guest');
      const isConnection = key.startsWith('connection');
      if ((isGuest && !guestsSimple) || (isConnection && !connectionsSimple)) continue;
      if (invalidMoney(values[key])) next[key] = nonNeg;
    }
    if (values.annualBaseMinor.trim() !== '') {
      if (invalidMoney(values.annualBaseMinor)) next.annualBaseMinor = nonNeg;
      if (invalidMoney(values.annualBaseWholesaleMinor)) next.annualBaseWholesaleMinor = nonNeg;
    }
    if (guestsSimple && invalidInt(values.guestsIncluded)) next.guestsIncluded = nonNeg;
    if (connectionsSimple && invalidInt(values.connectionsIncluded)) {
      next.connectionsIncluded = nonNeg;
    }
    if (!next.aiRechargeAmountMinor && parseMajor(values.aiRechargeAmountMinor, currency) <= 0) {
      next.aiRechargeAmountMinor = t(
        'customerRateCards.form.moreThanZero',
        'Must be more than zero.',
      );
    }
    if (
      !next.aiFirstPurchaseMinor &&
      !next.aiRechargeTriggerMinor &&
      parseMajor(values.aiFirstPurchaseMinor, currency) <=
        parseMajor(values.aiRechargeTriggerMinor, currency)
    ) {
      next.aiFirstPurchaseMinor = t(
        'customerRateCards.form.moreThanTrigger',
        'Must be more than the recharge trigger.',
      );
    }
    if (invalidPercent(values.aiCreditPlatformShareBp)) {
      next.aiCreditPlatformShareBp = t(
        'customerRateCards.form.between0And100',
        'Must be between 0 and 100.',
      );
    }
    if (invalidPct(values.aiWholesaleMarkupBp)) next.aiWholesaleMarkupBp = nonNeg;
    if (invalidPct(values.aiRetailMarkupBp)) next.aiRetailMarkupBp = nonNeg;
    if (invalidInt(values.trialDaysNewSubscriber)) next.trialDaysNewSubscriber = nonNeg;
    if (invalidInt(values.trialDaysGuestConversion)) next.trialDaysGuestConversion = nonNeg;
    return next;
  };

  const money = (key: PriceKey) => parseMajor(values[key], currency);
  const bp = (key: PriceKey) => Math.round(Number(values[key]) * 100);

  const buildBody = (): CustomerRateCardVersionWrite => {
    const annual = values.annualBaseMinor.trim() !== '';
    const body: CustomerRateCardVersionWrite = {
      baseCostMinor: money('baseCostMinor'),
      baseWholesaleMinor: money('baseWholesaleMinor'),
      baseMinor: money('baseMinor'),
      annualBaseMinor: annual ? money('annualBaseMinor') : 0,
      annualBaseWholesaleMinor: annual ? money('annualBaseWholesaleMinor') : 0,
      aiWholesaleMarkupBp: bp('aiWholesaleMarkupBp'),
      aiRetailMarkupBp: bp('aiRetailMarkupBp'),
      aiCreditPlatformShareBp: bp('aiCreditPlatformShareBp'),
      aiFirstPurchaseMinor: money('aiFirstPurchaseMinor'),
      aiRechargeTriggerMinor: money('aiRechargeTriggerMinor'),
      aiRechargeAmountMinor: money('aiRechargeAmountMinor'),
      trialDaysNewSubscriber: Number(values.trialDaysNewSubscriber),
      trialDaysGuestConversion: Number(values.trialDaysGuestConversion),
      version: version.version,
    };
    // Exactly one of the two credit fields: the server converts a cost; the console never does.
    if (creditBy === 'cost') body.aiMonthlyCreditCostMinor = money('aiMonthlyCredit');
    else body.aiMonthlyCreditMinor = money('aiMonthlyCredit');
    if (guestsSimple) {
      body.seatBands = simpleBandInputs(Number(values.guestsIncluded), {
        retailMinor: money('guestRetail'),
        wholesaleMinor: money('guestWholesale'),
        costMinor: money('guestCost'),
      });
    }
    if (connectionsSimple) {
      body.connectedAccountBands = simpleBandInputs(Number(values.connectionsIncluded), {
        retailMinor: money('connectionRetail'),
        wholesaleMinor: money('connectionWholesale'),
        costMinor: money('connectionCost'),
      });
    }
    return body;
  };

  const clientErrors = validate();
  const showErrors = (next: Record<string, string>) => {
    setSaveErrors(next);
    if (Object.keys(next).some((key) => (PRICE_KEYS as readonly string[]).includes(key))) {
      setAttempt((n) => n + 1);
    }
  };

  const submit = async () => {
    if (inFlight.current) return;
    showErrors(clientErrors);
    setFormError(null);
    if (Object.keys(clientErrors).length > 0) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      const saved = await adminApi.patchCustomerRateCardVersion(cardId, version.id, buildBody());
      setInitialSnapshot(snapshot);
      onSaved(saved);
    } catch (err) {
      if (isValidationFailed(err)) {
        const byServer = validationFields(err, t);
        const placed: Record<string, string> = {};
        let unplaced = false;
        for (const [field, message] of Object.entries(byServer)) {
          const key = priceKeyOf(field);
          if (key === null) unplaced = true;
          else placed[key] = message;
        }
        showErrors(placed);
        if (unplaced || Object.keys(placed).length === 0) {
          setFormError({
            message: errorCopy(
              err,
              t,
              t('customerRateCards.errors.save', "Couldn't save this version."),
            ),
            stale: false,
          });
        }
      } else if (err instanceof ApiError && String(err.code) === 'RATE_CARD_IMMUTABLE') {
        setFormError({
          message: t('customerRateCards.errors.immutable', 'Only a draft version can be edited.'),
          stale: true,
        });
      } else {
        setFormError({
          message: errorCopy(
            err,
            t,
            t('customerRateCards.errors.save', "Couldn't save this version."),
          ),
          stale: isStaleWrite(err),
        });
      }
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const messageFor = (key: PriceKey): string | undefined => saveErrors[key] || clientErrors[key];

  const field = (key: PriceKey, opts: { caption?: string; required?: boolean } = {}) => {
    const message = messageFor(key);
    return (
      <FormField key={key} width={VALUE_WIDTH}>
        <FieldWithHelp label={labels[key]} help={help[key]}>
          <TextField
            id={fieldId(key)}
            label={labels[key]}
            value={values[key]}
            onChange={(event) => update(key, event.target.value)}
            error={Boolean(message)}
            helperText={message ?? opts.caption}
            required={opts.required ?? false}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
            fullWidth
          />
        </FieldWithHelp>
      </FormField>
    );
  };

  const bandsSection = (
    kind: 'guests' | 'connections',
    bands: AdminBandView[],
    simple: boolean,
  ) => {
    const isGuests = kind === 'guests';
    if (!simple) {
      return (
        <>
          <Alert severity="info">
            {t(
              'rateCard.prices.tiered',
              'This version has tiered bands. They are shown read-only and saving keeps them as they are.',
            )}
          </Alert>
          <BandsTable bands={bands} currency={currency} />
        </>
      );
    }
    return (
      <>
        {field(isGuests ? 'guestsIncluded' : 'connectionsIncluded', { required: true })}
        <FormRow>
          {field(isGuests ? 'guestCost' : 'connectionCost', { required: true })}
          {field(isGuests ? 'guestWholesale' : 'connectionWholesale', { required: true })}
          {field(isGuests ? 'guestRetail' : 'connectionRetail', { required: true })}
        </FormRow>
      </>
    );
  };

  const summary: ErrorSummaryItem[] = PRICE_KEYS.flatMap((key) => {
    const message = saveErrors[key];
    return message ? [{ id: fieldId(key), label: labels[key], message }] : [];
  });

  const requestCancel = () => {
    if (submitting) return;
    if (dirty) setDiscarding(true);
  };

  return (
    <>
      <FormPage
        headerless
        title={title}
        onSubmit={() => void submit()}
        busy={submitting}
        submitLabel={t('customerRateCards.actions.saveDraft', 'Save draft')}
        onCancel={requestCancel}
        guard={guard}
        errorSummary={{ items: attempt > 0 ? summary : [], attempt }}
        formError={
          formError ? (
            <Alert
              severity="error"
              action={
                formError.stale ? (
                  <Button color="inherit" size="small" onClick={onReload}>
                    {t('common.reload', 'Reload')}
                  </Button>
                ) : undefined
              }
            >
              {formError.message}
            </Alert>
          ) : null
        }
      >
        <FormSection title={t('rateCard.prices.section.base', 'Base, per month')}>
          <FormRow>
            {field('baseCostMinor', { required: true })}
            {field('baseWholesaleMinor', { required: true })}
            {field('baseMinor', { required: true })}
          </FormRow>
        </FormSection>
        <FormSection title={t('rateCard.prices.section.annual', 'Base, per year')}>
          <FormRow>
            {field('annualBaseWholesaleMinor')}
            {field('annualBaseMinor', {
              ...(values.annualBaseMinor.trim() === ''
                ? {
                    caption: t(
                      'resellerCards.form.annualEmpty',
                      'Leave empty to offer monthly only.',
                    ),
                  }
                : {}),
            })}
          </FormRow>
          <Typography variant="body2" color="text.secondary">
            {t(
              'rateCard.prices.annualCostNote',
              'Our annual cost is the monthly cost × 12; the worksheet uses it.',
            )}
          </Typography>
        </FormSection>
        <FormSection title={t('rateCard.prices.section.guests', 'Guests')}>
          {bandsSection('guests', version.seatBands, guestsSimple)}
        </FormSection>
        <FormSection title={t('rateCard.prices.section.connections', 'Bank connections')}>
          {bandsSection('connections', version.connectedAccountBands, connectionsSimple)}
        </FormSection>
        <FormSection title={t('rateCards.ai', 'AI')}>
          <FormRow>
            {field('aiWholesaleMarkupBp', { required: true })}
            {field('aiRetailMarkupBp', { required: true })}
          </FormRow>
          <RadioGroup
            row
            aria-label={t('rateCard.prices.creditBy', 'Set the monthly credit by')}
            value={creditBy}
            onChange={(event) => {
              setCreditBy(event.target.value === 'cost' ? 'cost' : 'retail');
              update(
                'aiMonthlyCredit',
                event.target.value === 'cost' ? '' : values.aiMonthlyCredit,
              );
            }}
          >
            <FormControlLabel
              value="retail"
              control={<Radio />}
              label={t('rateCard.prices.creditByRetail', 'Customer price')}
            />
            <FormControlLabel
              value="cost"
              control={<Radio />}
              label={t('rateCard.prices.creditByCost', 'Our cost')}
            />
          </RadioGroup>
          {field('aiMonthlyCredit', {
            required: true,
            caption:
              creditBy === 'cost'
                ? t(
                    'rateCard.prices.creditCostCaption',
                    'We convert our cost with the markups above. Saved now: {{amount}} at the customer price.',
                    { amount: formatMoney(version.aiMonthlyCreditMinor, currency, i18n.language) },
                  )
                : t('rateCard.prices.creditRetailCaption', 'The credit at the customer price.'),
          })}
          {field('aiCreditPlatformShareBp', { required: true })}
          <FormRow>
            {field('aiFirstPurchaseMinor', { required: true })}
            {field('aiRechargeTriggerMinor', { required: true })}
            {field('aiRechargeAmountMinor', { required: true })}
          </FormRow>
        </FormSection>
        <FormSection title={t('rateCards.form.section.trials', 'Trials')}>
          <FormRow>
            {field('trialDaysNewSubscriber', { required: true })}
            {field('trialDaysGuestConversion', { required: true })}
          </FormRow>
        </FormSection>
      </FormPage>
      <ConfirmDialog
        open={discarding}
        title={t('components.unsaved.title', 'Leave without saving?')}
        consequence={t('components.unsaved.consequence', 'Your changes to {{name}} will be lost.', {
          name: title,
        })}
        confirmLabel={t('components.unsaved.leave', 'Leave')}
        cancelLabel={t('components.unsaved.stay', 'Stay')}
        destructive
        onConfirm={() => {
          setDiscarding(false);
          setValues(initialValues(version, currency));
          setCreditBy('retail');
          setSaveErrors({});
        }}
        onCancel={() => setDiscarding(false)}
      />
    </>
  );
}

/** A version's bands as sent, read-only: from, to, then retail, wholesale and our cost per unit. */
function BandsTable({ bands, currency }: { bands: AdminBandView[]; currency: string }) {
  const { t } = useCopy();
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small" aria-label={t('rateCard.prices.bandsAria', 'Bands')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('rateCard.prices.bandFrom', 'From')}</TableCell>
            <TableCell>{t('rateCard.prices.bandTo', 'To')}</TableCell>
            <TableCell align="right">
              {t('rateCard.prices.bandRetail', 'Retail flat + per unit')}
            </TableCell>
            <TableCell align="right">
              {t('rateCard.prices.bandWholesale', 'Wholesale flat + per unit')}
            </TableCell>
            <TableCell align="right">
              {t('rateCard.prices.bandCost', 'Our cost flat + per unit')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bands.map((band) => (
            <TableRow key={band.from}>
              <TableCell>{band.from}</TableCell>
              <TableCell>{band.to ?? t('rateCard.prices.bandOpen', 'and up')}</TableCell>
              <TableCell align="right">
                <Amount minor={band.flatMinor} currency={currency} /> +{' '}
                <Amount minor={band.perUnitMinor} currency={currency} />
              </TableCell>
              <TableCell align="right">
                <Amount minor={band.wholesaleFlatMinor} currency={currency} /> +{' '}
                <Amount minor={band.wholesalePerUnitMinor} currency={currency} />
              </TableCell>
              <TableCell align="right">
                <Amount minor={band.costFlatMinor} currency={currency} /> +{' '}
                <Amount minor={band.costPerUnitMinor} currency={currency} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/** A published version's prices, read-only: each line's our cost, wholesale and retail as sent. */
function PricesSummary({
  version,
  currency,
}: {
  version: CustomerRateCardVersionView;
  currency: string;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const guests = simpleBands(version.seatBands);
  const connections = simpleBands(version.connectedAccountBands);
  const row = (
    key: string,
    label: string,
    cost: number | null,
    wholesale: number,
    retail: number,
  ) => (
    <TableRow key={key}>
      <TableCell component="th" scope="row">
        {label}
      </TableCell>
      <TableCell align="right">
        {cost === null ? NOT_SENT : <Amount minor={cost} currency={currency} />}
      </TableCell>
      <TableCell align="right">
        <Amount minor={wholesale} currency={currency} />
      </TableCell>
      <TableCell align="right">
        <Amount minor={retail} currency={currency} />
      </TableCell>
    </TableRow>
  );
  return (
    <>
      <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
        <Table size="small" aria-label={t('rateCard.prices.summaryAria', 'Prices')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('rateCard.prices.line', 'Line')}</TableCell>
              <TableCell align="right">{t('rateCard.prices.ourCost', 'Our cost')}</TableCell>
              <TableCell align="right">{t('rateCard.prices.wholesale', 'Wholesale')}</TableCell>
              <TableCell align="right">{t('rateCard.prices.retail', 'Retail')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {row(
              'base',
              t('rateCard.prices.section.base', 'Base, per month'),
              version.baseCostMinor,
              version.baseWholesaleMinor,
              version.baseMinor,
            )}
            {version.annualBaseMinor !== null
              ? row(
                  'annual',
                  t('rateCard.prices.section.annual', 'Base, per year'),
                  null,
                  version.annualBaseWholesaleMinor,
                  version.annualBaseMinor,
                )
              : null}
            {guests && version.seatBands.length > 0
              ? row(
                  'guests',
                  t('rateCard.grid.perGuestOver', 'Per guest over {{n, number}}', {
                    n: guests.included,
                  }),
                  guests.perUnit.costMinor,
                  guests.perUnit.wholesaleMinor,
                  guests.perUnit.retailMinor,
                )
              : null}
            {connections && version.connectedAccountBands.length > 0
              ? row(
                  'connections',
                  t('rateCard.grid.perConnectionOver', 'Per connection over {{n, number}}', {
                    n: connections.included,
                  }),
                  connections.perUnit.costMinor,
                  connections.perUnit.wholesaleMinor,
                  connections.perUnit.retailMinor,
                )
              : null}
          </TableBody>
        </Table>
      </TableContainer>
      {guests === null ? <BandsTable bands={version.seatBands} currency={currency} /> : null}
      {connections === null ? (
        <BandsTable bands={version.connectedAccountBands} currency={currency} />
      ) : null}
      <Typography variant="body2" sx={{ mt: 2 }}>
        {t(
          'rateCard.prices.aiSummary',
          'AI: wholesale markup {{wholesale}} on provider cost, retail markup {{retail}} on wholesale. Monthly credit {{credit}} at the customer price; we absorb {{share}}.',
          {
            wholesale: formatPercentBp(version.aiWholesaleMarkupBp, locale),
            retail: formatPercentBp(version.aiRetailMarkupBp, locale),
            credit: formatMoney(version.aiMonthlyCreditMinor, currency, locale),
            share: formatPercentBp(version.aiCreditPlatformShareBp, locale),
          },
        )}
      </Typography>
    </>
  );
}
